import { Guid } from 'Lib/Guid';
import isPojo from '../../Lib/Object/isPojo';
import { camelizeObjectKeys } from './rxjsHelpers';

export type ErrorMessage = {
  additionalInformation: any;
  apiCallId?: Guid;
  code?: string;
  details?: string;
  message: string;
  error: any;
  serverTime?: string;
};

export const isErrorMessage = (arg: any): arg is ErrorMessage =>
  arg && !(arg instanceof Error) && (arg.message || arg.additionalInformation);

export const errorToString = (error: any) => {
  if (error && typeof error.Error === 'object' && error.Error.Message) {
    // If a valid QAPIErrorResponse is provided with a non null + non empty Message
    return error.Error.Message;
  } else if (typeof error === 'string') {
    // If error is a string
    return error;
  } else if (error.name && error.message) {
    // If error looks like a javascript Error object
    if (error?.message?.toLowerCase() === 'failed to fetch')
      return `${error.message}. The QEST Web API may not be accessible.`;
    return error.message;
  } else {
    // Else it is an unrecognized error.
    console.error(`An unrecognized error occured '${error}'`, error);
    return 'An unrecognized error occurred.';
  }
};

export const errorAdditionalInformation = (error: any) => {
  if (error && typeof error.Error === 'object' && error.Error.AdditionalInformation) {
    const camelized: any = camelizeObjectKeys(error.Error.AdditionalInformation);
    Object.keys(camelized).forEach(
      (k: any) => (camelized[k] = isPojo(camelized[k]) ? camelizeObjectKeys(camelized[k]) : camelized[k])
    );
    return camelized;
  }
  return {};
};

export const sanitizeErrorObject = (error: any): ErrorMessage => {
  if (!error || isErrorMessage(error)) return error;

  const { Error, message, DetailedError, additionalInformation, ...otherFirstLevelErrorProps } = error;
  if (error.Error) {
    const { AdditionalInformation, Message, StackTraceString, Code, ...otherSecondLevelErrorProps } = error.Error;
    return {
      additionalInformation: camelizeObjectKeys(errorAdditionalInformation(error)),
      apiCallId: AdditionalInformation?.ApiCallId,
      code: Code,
      details: DetailedError ?? `${Message}\r\n${StackTraceString}`,
      error: { ...camelizeObjectKeys(otherFirstLevelErrorProps), ...camelizeObjectKeys(otherSecondLevelErrorProps) },
      message: errorToString(error),
      serverTime: AdditionalInformation?.ServerTime,
    } as ErrorMessage;
  } else {
    return {
      additionalInformation: camelizeObjectKeys(errorAdditionalInformation(error)),
      // If we received an error without a code, set the code to 460. This is an unused code which is used to define
      // when we cannot connect to the Web Api (Internet Disconnected etc).
      code: error && error.name ? 460 : undefined,
      message: errorToString(error),
      error: { ...camelizeObjectKeys(otherFirstLevelErrorProps) },
    } as ErrorMessage;
  }
};

export const getErrorMessageFromMessage = (message: string): ErrorMessage => ({
  message,
  error: undefined,
  additionalInformation: undefined,
});

export enum ApiErrorCodes {
  ClientErrorConflict = 409,
}
